import { useCallback, useMemo } from 'react';
import { UseFormReset } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useElementActions } from 'bom/hooks';
import { IElement, IElementTemplate } from 'bom/types';
import { Form, FormField } from 'core/components';
import { InputType, NotificationPosition } from 'core/enums';
import { FormProvider, useNotificationContext } from 'core/states';
import { preventEnter } from 'core/utils';

import { saveElementAsTemplateSchema } from './schema';
import { SeeElementTemplateButton } from './SeeElementTemplateButton';
import { ISaveElementAsTemplateFormData } from './types';

interface IProps {
  isOpen: boolean;
  onClose: () => void;
  element: IElement;
  onSubmitSuccess: () => void;
}

export const SaveAsTemplate = ({ isOpen, onClose, element, onSubmitSuccess }: IProps) => {
  const { t } = useTranslation();

  const { showSuccessNotification, clearNotification } = useNotificationContext();
  const { saveElementAsTemplate } = useElementActions();

  const defaultValues = useMemo(
    () => ({
      customIdentifier: '',
      description: element.name,
    }),
    [element.name]
  );

  const handleOnSubmitSuccess = useCallback(
    (elementTemplate: IElementTemplate) => {
      onSubmitSuccess();

      showSuccessNotification(
        {
          message: t('messageElementSaveAsTemplateSuccess', {
            templateDescription: elementTemplate.description,
          }),
          buttons: [
            <SeeElementTemplateButton
              key="seeElementTemplate"
              elementTemplateId={elementTemplate.id}
            />,
          ],
          disableAutoClose: true,
        },
        NotificationPosition.TOP
      );
    },
    [onSubmitSuccess, showSuccessNotification, t]
  );

  const handleOnClose = useCallback(
    (reset: UseFormReset<ISaveElementAsTemplateFormData>) => {
      reset();
      clearNotification();
      onClose();
    },
    [clearNotification, onClose]
  );

  const onSubmit = useCallback(
    (reset: UseFormReset<ISaveElementAsTemplateFormData>) =>
      async (data: ISaveElementAsTemplateFormData) => {
        const { success, elementTemplate } = await saveElementAsTemplate({
          elementTemplate: {
            customIdentifier: data.customIdentifier,
            description: data.description,
          },
          sourceElementId: Number(element.id),
        });

        if (!success) {
          return;
        }

        handleOnClose(reset);

        setTimeout(() => {
          handleOnSubmitSuccess(elementTemplate);
        }, 500);
      },
    [element.id, handleOnClose, handleOnSubmitSuccess, saveElementAsTemplate]
  );

  return (
    <FormProvider
      schema={saveElementAsTemplateSchema()}
      defaultValues={defaultValues}
      resetDefaultValues
    >
      <Form
        isOpen={isOpen}
        onClose={handleOnClose}
        title={t('titleCreateElementTemplate')}
        subtitle={element.name}
        onSubmit={onSubmit}
        submitBtnLabel={t('buttonLabelCreateElementTemplate')}
      >
        <FormField
          name="customIdentifier"
          type={InputType.TEXT_INPUT}
          inputProps={{ label: t('labelCustomIdentifier'), required: true }}
        />

        <FormField
          name="description"
          type={InputType.TEXTAREA}
          inputProps={{
            label: t('labelDescription'),
            onKeyDown: preventEnter,
            maxRows: 6,
            required: true,
          }}
        />
      </Form>
    </FormProvider>
  );
};
